import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import Toast, {ToastProps} from '../components/Toast/Toast'

export type ToastConfig = Omit<ToastProps, 'open' | 'onOpenChange'>

type RequiredToastConfig = ToastConfig & {
  title: string
  description: React.ReactNode | string
}

interface ToastContextValue {
  showToast: (config: RequiredToastConfig) => void
  hideToast: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const initialState: ToastConfig & {open: boolean} = {
  open: false,
  title: '',
  description: '',
  variant: 'info',
  duration: 4000,
  overlayMode: 'none',
  dismissible: true,
  overlayClickable: true,
  className: '',
  closeLabel: 'Close',
  autoDismiss: true,
}

export function ToastProvider({children}: {children: ReactNode}) {
  const [toastState, setToastState] = useState<ToastConfig & {open: boolean}>(initialState)

  const hideToast = useCallback(() => {
    setToastState((prev) => ({...prev, open: false}))
  }, [])

  const showToast = useCallback((config: RequiredToastConfig) => {
    setToastState({...initialState, ...config, open: true})
  }, [])

  const value = useMemo(
    () => ({
      showToast,
      hideToast,
    }),
    [showToast, hideToast]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        {...toastState}
        onOpenChange={(open) => {
          if (!open) hideToast()
        }}
        className="max-h-[500px] overflow-y-auto"
      />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return ctx
}
